/**
 * Design Your Own bar custom element for showing progress steps
 * 
 * @module design-your-own
 * @version 1.0.0
 * @extends HTMLElement
 */
class DesignYourOwnBar extends HTMLElement {

  /**
   * Constructor for the DesignYourOwnBar element
   */
  constructor() {
    super();
    this.api = null;
    this.boundApiListener = this.onApiChange.bind(this);
  }

  /**
   * Connected callback - runs when element is added to DOM
   */
  connectedCallback() {
    // Wait for API to be available
    this.initializeApi();
    
    // Render initial state
    this.render();

    // Initialize events
    this.initEvents();
  }

  /**
   * Disconnected callback - cleanup when element is removed
   */
  disconnectedCallback() {
    if (this.api) {
      this.api.removeEventListener(this.boundApiListener);
    }
  }

  /**
   * Initialize API connection
   */
  initializeApi() {
    // Wait for API to be available
    const checkApi = () => {
      if (window.theme && window.theme.dyo) {
        this.api = window.theme.dyo;
        this.api.addEventListener(this.boundApiListener);
        this.render();
      } else {
        setTimeout(checkApi, 100);
      }
    };
    checkApi();
  }

  /**
   * Handle API state changes
   */
  onApiChange(event, data) {
    if (event === 'step-changed' || event === 'setting-selected' || event === 'diamond-selected' || event === 'setting-reset' || event === 'diamond-reset' || event === 'reset') {
      this.render();
    }
  }

  /**
   * Get SVG icon for each step
   */
  getStepIcon(stepId) {
    const icons = {
      settings: `
        <svg width="100%" height="100%" viewBox="0 0 35 35" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M17.4274 31.8241C22.9043 31.8241 27.3441 27.3842 27.3441 21.9074C27.3441 16.4306 22.9043 11.9907 17.4274 11.9907C11.9506 11.9907 7.51074 16.4306 7.51074 21.9074C7.51074 27.3842 11.9506 31.8241 17.4274 31.8241Z" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M23.0941 4.87649L22.9345 6.52083L17.3476 11.8333L11.7607 6.52083V4.75" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      `,
      diamonds: `
        <svg width="100%" height="100%" viewBox="0 0 35 35" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M4.72207 11.8335H32.3471M3.88668 12.6443L17.6826 30.5381C17.7828 30.6689 17.9118 30.7747 18.0596 30.8476C18.2073 30.9205 18.3698 30.9584 18.5346 30.9584C18.6993 30.9584 18.8618 30.9205 19.0096 30.8476C19.1573 30.7747 19.2863 30.6689 19.3866 30.5381L33.1825 12.6443C33.3181 12.4677 33.3968 12.254 33.4081 12.0316C33.4193 11.8092 33.3626 11.5886 33.2455 11.3992L28.5659 3.84549C28.4696 3.68917 28.3349 3.56007 28.1746 3.47046C28.0143 3.38085 27.8338 3.3337 27.6502 3.3335H9.41898C9.23536 3.3337 9.05484 3.38085 8.89456 3.47046C8.73429 3.56007 8.59958 3.68917 8.50324 3.84549L3.82359 11.3992C3.70649 11.5886 3.64981 11.8092 3.66107 12.0316C3.67234 12.254 3.75103 12.4677 3.88668 12.6443Z" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M28.0967 4.396L24.9092 11.8335M24.9092 11.8335L18.5342 3.3335L12.1592 11.8335M24.9092 11.8335L18.5342 29.896L12.1592 11.8335M8.97168 4.396L12.1592 11.8335" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      `,
      review: `
        <svg width="100%" height="100%" viewBox="0 0 35 35" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M17.913 31.8241C23.3898 31.8241 27.8297 27.3842 27.8297 21.9074C27.8297 16.4306 23.3898 11.9907 17.913 11.9907C12.4362 11.9907 7.99634 16.4306 7.99634 21.9074C7.99634 27.3842 12.4362 31.8241 17.913 31.8241Z" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M21.2186 3.17578L23.4223 5.37949L17.9131 11.9906L12.4038 5.37949L14.6075 3.17578H21.2186Z" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      `
    };
    return icons[stepId] || '';
  }

  /**
   * Render a step item
   */
  renderStep(step, index, isLast = false) {

    if (!this.api) return '';

    // Get the data for the step
    const data = this.api.data[step.id === 'settings' ? 'setting' : step.id === 'diamonds' ? 'diamond' : null];
    
    // Get the status of the step
    const isCompleted = step.status === 'completed';
    const isActive = step.status === 'active';
    const isReview = step.id === 'review';

    // Special rendering for review step when configuration is complete
    if (isReview && this.api.isComplete()) {
      const created = this.api.data.createdProduct;
      const reviewUrl = created ? created.url : null;
      const isOnReviewPage = reviewUrl && window.location.pathname === reviewUrl || document.querySelector('dyo-product-review') !== null;

      let actionHtml;
      if (isOnReviewPage) {
        actionHtml = '<span>Congratulations, Your Custom Ring is Ready!</span>';
      } else if (created) {
        actionHtml = `<a href="${reviewUrl}">Congratulations, Your Custom Ring is Ready!</a>`;
      } else {
        actionHtml = '<button class="dyo-bar__review-btn">Congratulations, Your Custom Ring is Ready!</button>';
      }

      return `
        <div data-review-step="true" data-step="${step.id}" data-status="${step.status}">
          <div class="dyo-bar__icon">
            ${this.getStepIcon(step.id)}
          </div>
          <div class="dyo-bar__content">
            <div class="dyo-bar__actions">
              ${actionHtml}
            </div>
          </div>
        </div>
      `;
    }

    // Render step with data if available
    if (data && (isCompleted || isActive)) {
      // Get the correct edit URL based on step type
      let editUrl = '#';
      if (step.id === 'settings') {
        editUrl = '/collections/design-your-own-engagement-ring';
      } else if (step.id === 'diamonds' && data.type) {
        const stoneUrls = {
          'diamond': '/collections/loose-diamonds',
          'gemstone': '/collections/gemstones',
          'moissanite': '/collections/moissanites'
        };
        editUrl = stoneUrls[data.type] || '/collections/loose-diamonds';
      }

      return `
        <div data-step="${step.id}" data-status="${step.status}">
          <div class="dyo-bar__icon">
            <img src="${data.image}" alt="${data.title}">
          </div>
          <div class="dyo-bar__content">
            <span class="dyo-bar__label-primary">${data.title}</span>
            <div class="dyo-bar__actions">
              <span class="dyo-bar__label-secondary">${ window.theme.money.format(data.totalPrice, true) }</span>
              <a href="${editUrl}" class="dyo-bar__action-button" data-edit-step="${step.id}">Edit</a>
            </div>
          </div>
        </div>
      `;
    }

    // Render default step
    const stepLabels = {
      'settings': { primary: 'Pick', secondary: 'Setting' },
      'diamonds': { primary: 'Pick', secondary: 'Stone' },
      'review': { primary: 'Review', secondary: 'Your Ring' }
    };
    
    const labels = stepLabels[step.id];
    
    return `
      <div data-step="${step.id}" data-status="${step.status}">
        <div class="dyo-bar__icon">
          ${this.getStepIcon(step.id)}
        </div>
        <div class="dyo-bar__content">
          <span class="dyo-bar__label-primary">${labels.primary}</span>
          <span class="dyo-bar__label-secondary">${labels.secondary}</span>
        </div>
      </div>
    `;
  }

  /**
   * Handle edit button clicks
   */
  handleEditClick(event) {
    if (event.target.matches('[data-edit-step]')) {
      const stepId = event.target.getAttribute('data-edit-step');
      
      // Special handling for settings and diamonds steps - allow redirect and reset
      if (stepId === 'settings' || stepId === 'diamonds') {
        // Let the redirect happen naturally, but first reset the API state
        if (this.api) {
          this.api.editStep(stepId);
        }
        // Don't prevent default - let the href work for redirect
        return;
      }
      
      // For other steps, prevent default and use API navigation
      event.preventDefault();
      if (this.api) {
        this.api.editStep(stepId);
      }
    }
  }

  /**
   * Handle review button click - create product via API and redirect
   */
  async handleReviewClick(btn) {
    const originalText = btn.textContent;
    try {
      btn.disabled = true;
      btn.textContent = 'Creating your ring...';

      const setting = this.api.data.setting;
      const productData = await this.api.createProduct();
      this.api.setCreatedProduct(productData);

      const params = new URLSearchParams();
      if (setting) {
        if (setting.size) params.set('size', setting.size.split(':')[0]);
        if (setting.warranty) params.set('warranty', setting.warranty.split(':')[0]);
        if (setting.engraving) params.set('engraving_text', setting.engraving);
        if (setting.fontStyle) params.set('engraving_font', setting.fontStyle);
        if (setting.appraisal && setting.appraisal.split(':')[0] === 'true') params.set('appraisal', 'true');
      }

      const qs = params.toString();
      window.location.href = `/products/${productData.handle}${qs ? '?' + qs : ''}`;
    } catch (error) {
      console.error('DYO product creation failed:', error);
      btn.disabled = false;
      btn.textContent = originalText;
      window.location.href = this.api.getShareableUrl();
    }
  }

  /**
   * Render the complete bar structure
   */
  render() {

    if (!this.api) {
      this.innerHTML = '<div class="dyo-bar__loading">Loading...</div>';
      return;
    }

    this.dataset.startedWith = this.api.startedWith;
    this.dataset.nextStep = this.api.nextStep;

    const steps = this.api.getSteps();
    const stepsHtml = steps.map((step, index) => {
      return this.renderStep(step, index, index === steps.length - 1);
    }).join('');

    this.innerHTML = `<div class="container desktop:px-4">${stepsHtml}</div>`;

    const pathname = window.location.pathname;
    const isOnReviewPage = pathname === '/products/custom-ring';
    const isOnSettingsPage = pathname === '/collections/design-your-own-engagement-ring';

    const diamondsPages = [
      '/collections/loose-diamonds',
      '/products/diamond'
    ];

    const isOnDiamondsPage = diamondsPages.includes(pathname);

    this.dataset.page = isOnReviewPage ? 'review' : isOnSettingsPage ? 'settings' : isOnDiamondsPage ? 'diamonds' : '';

    // Add event listener for review button (API create + redirect)
    const reviewBtn = this.querySelector('.dyo-bar__review-btn');
    if (reviewBtn) {
      reviewBtn.addEventListener('click', () => this.handleReviewClick(reviewBtn));
    }

    // Add event listeners for edit buttons
    this.removeEventListener('click', this.handleEditClick);
    this.addEventListener('click', this.handleEditClick.bind(this));
  }

  /**
   * Initialize events
   */
  initEvents() {
    
    // Listen for DYO updates
    if (window.theme?.dyo) {
      window.theme.dyo.addEventListener(this.handleDYOUpdate.bind(this));
    }
  }

  /**
   * Handle DYO updates
   * 
   * @param {string} event The event name
   */
  handleDYOUpdate(event) {
    if (event === 'setting-modified') {

      this.classList.add('loading');

      setTimeout(() => {
        this.render();
      }, 300);

      setTimeout(() => {
        this.classList.remove('loading');
      }, 500);
    }
  }
}

// Define the custom element
customElements.define('dyo-bar', DesignYourOwnBar);
